import React, { useCallback, useState } from 'react';

import { Box } from '@rbilabs/components-library';
import delv from 'dlv';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import GiftCardInfo from 'components/gift-card-info';
import loadPrepaidCardModal from 'components/load-prepaid-card-modal';
import SelectPaymentMethod, { HoverTheme } from 'components/payment-method-select';
import { IRadioGroupButton, RadioGroup, RadioGroupButton } from 'components/radio-group';
import TextButton from 'components/text-button';
import WarningMessage from 'components/warning-message';
import { useSetGiftCardReloadAccountMutation } from 'generated/graphql-gateway';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import useDialogModal from 'hooks/use-dialog-modal';
import AuthRequired from 'pages/authentication/auth-required';
import { getNativePaymentDisclaimer } from 'pages/cart/payment/order-payment/utils';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePaymentContext } from 'state/payment';
import {
  DEFAULT_RELOAD_AMOUNT,
  PREPAID_RELOAD_AMOUNTS,
  RELOAD_AMOUNTS,
} from 'state/payment/constants';
import useReloadTimCard from 'state/payment/hooks/use-reload-tim-card';
import { IApplePayDetails, IGooglePayDetails, IPaymentMethod } from 'state/payment/types';
import { getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';

import {
  ActionContainer,
  Column,
  FundingSourceSection,
  GiftCardInfoContainer,
  Heading,
  PaymentMethodContainer,
  Separator,
  SubHeadingCentered,
  WarningContainer,
  Wrapper,
} from './styled';

const INITIAL_LOAD_AMOUNT = 500;

interface IRadioGroupBase {
  isLoading: boolean;
  onSetActive: (id: string | number) => any;
  onSetSelected: (id: string | number) => any;
}

export interface IRadioGroupItem extends IRadioGroupBase {
  selected: number | string;
  options: IRadioGroupButton[];
}

export interface IRadioGroupItemPaymentMethod {
  isLoading: boolean;
  selected?: IPaymentMethod;
  options: IPaymentMethod[];
  setPrepaidReloadPaymentMethodId: (fdAccountId: string) => void;
}

export interface ITimCardPage {
  pageRoutes: any;

  // Gift Card
  isLoadingGiftCard: boolean;
  giftCardNumberFormatted: string;
  giftCardBallanceCents: number;

  // Payment Methods
  paymentMethods: IRadioGroupItemPaymentMethod;

  giftCardReload: IRadioGroupItem;

  onPaymentMethodClose: () => void;
  onAddPaymentMethodClick: () => void;
  onOpenTransferBallanceModal: () => void;
}

const makeRadioGroupButtons = (items: IRadioGroupButton[]) => {
  return items.map((obj: IRadioGroupButton) => (
    <RadioGroupButton key={obj.value} label={obj.label} value={obj.value} />
  ));
};

export const TimCardPage = ({
  pageRoutes,
  isLoadingGiftCard,
  giftCardNumberFormatted,
  giftCardBallanceCents,
  paymentMethods,
  giftCardReload,
  onOpenTransferBallanceModal,
  onAddPaymentMethodClick,
}: ITimCardPage) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { allowPrepaidApplePay, allowPrepaidGooglePay } = usePrepaidsPaymentMethods();

  const hasReloadMethods = paymentMethods.options.length || paymentMethods.selected;
  const nativePaymentDisclaimer = getNativePaymentDisclaimer(
    allowPrepaidApplePay,
    allowPrepaidGooglePay,
    formatMessage
  );

  return (
    <AuthRequired>
      <Wrapper>
        <Heading>{formatMessage({ id: 'myTimsGiftCard' })}</Heading>
        <Column>
          <GiftCardInfoContainer>
            <GiftCardInfo
              isLoading={isLoadingGiftCard}
              cardNumberFormatted={giftCardNumberFormatted}
              totalCents={giftCardBallanceCents}
            />
          </GiftCardInfoContainer>
          {!isLoadingGiftCard && (
            <>
              <Separator />
              <SubHeadingCentered>{formatMessage({ id: 'loadCard' })}</SubHeadingCentered>
              <RadioGroup
                onChange={giftCardReload.onSetSelected}
                selected={giftCardReload.selected}
              >
                {makeRadioGroupButtons(giftCardReload.options)}
              </RadioGroup>

              <PaymentMethodContainer>
                <SelectPaymentMethod
                  selectedMethod={paymentMethods.selected}
                  nonSelectedMethods={paymentMethods.options}
                  onOptionClick={paymentMethods.setPrepaidReloadPaymentMethodId}
                  hoverTheme={HoverTheme.LIGHT}
                  onNavigateClick={(e, route) => navigate(route)}
                />
              </PaymentMethodContainer>
              <ActionContainer>
                <ActionButton
                  data-testid="tim-card-reload-button"
                  onClick={() => giftCardReload.onSetActive(giftCardReload.selected)}
                  isLoading={false}
                  loadingColor={theme.token('icon-active')}
                  disabled={!hasReloadMethods}
                  skipLogEvent
                >
                  {formatMessage({ id: 'loadNow' })}
                </ActionButton>
                <TextButton onClick={onOpenTransferBallanceModal}>
                  {formatMessage({ id: 'transferBalanceFromAnotherTimsGiftCard' })}
                </TextButton>
              </ActionContainer>
              {!paymentMethods.selected && nativePaymentDisclaimer && (
                <WarningContainer>
                  <WarningMessage message={nativePaymentDisclaimer} />
                </WarningContainer>
              )}
              <FundingSourceSection>
                {!paymentMethods.isLoading && (
                  <Box maxWidth="400px" width="100%" margin="1.5rem auto 0">
                    <ActionButton
                      transparent
                      variant={ActionButtonVariants.OUTLINE}
                      fullWidth
                      to={pageRoutes.payment}
                      onClick={onAddPaymentMethodClick}
                    >
                      {formatMessage({ id: 'goToPaymentMethods' })}
                    </ActionButton>
                  </Box>
                )}
              </FundingSourceSection>
            </>
          )}
        </Column>
      </Wrapper>
    </AuthRequired>
  );
};

const TimCard = () => {
  const { formatMessage } = useIntl();

  const { user } = useAuthContext();
  // Payment methods
  const {
    getPrepaidPaymentMethod,
    getBalanceFromPaymentMethods,
    getPrepaidCardNumber,
    loading: paymentsLoading,
    setPrepaidReloadPaymentMethodId,
    syncDefaultReloadPaymentMethodId,
    prepaidReloadPaymentMethodId: reloadPaymentMethodId,
    openTransferBalanceDialog,
  } = usePaymentContext();
  const {
    nonSelectedReloadPaymentMethods,
    selectedReloadPaymentMethod,
  } = usePrepaidsPaymentMethods();
  const { reloadGiftCard } = useReloadTimCard();

  const enableGiftCardMutations = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD_LOAD_MUTATION);
  const [fireSetGiftCardReloadAccount] = useSetGiftCardReloadAccountMutation();

  const handleReloadPaymentMethodSelect = async (accountIdentifier: string) => {
    setPrepaidReloadPaymentMethodId(accountIdentifier);
    if (enableGiftCardMutations) {
      try {
        await fireSetGiftCardReloadAccount({
          variables: { input: { accountId: accountIdentifier } },
        });
        syncDefaultReloadPaymentMethodId(accountIdentifier);
      } catch (error) {
        logger.error({ error, message: 'Error updating gift card reload account' });
      }
    }
  };

  // Reload card confirmation modal
  const [forceSuccessDialog, setForceSuccessDialog] = useState(false);
  const [currentTotalTimBalance, setCurrentTotalTimBalance] = useState(INITIAL_LOAD_AMOUNT);
  const [LoadPrepaidCardDialog, openLoadPrepaidCardDialog] = useDialogModal<
    {},
    { loadAmount: number; forceSuccess?: boolean }
  >({
    Component: loadPrepaidCardModal,
  });

  // User details
  const billingCountry = delv(user!, 'details.isoCountryCode');
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const userReloadAmt = delv(user!, 'details.defaultReloadAmt');

  // Local State
  // -----------
  // Set the default active state
  const [reloadAmount, setReloadAmount] = useState(() => {
    if (!userReloadAmt) {
      return DEFAULT_RELOAD_AMOUNT;
    }
    return PREPAID_RELOAD_AMOUNTS.includes(userReloadAmt) ? userReloadAmt : DEFAULT_RELOAD_AMOUNT;
  });

  // Actions
  // -------
  // use the local state to make api calls

  const prepaidPaymentMethod = getPrepaidPaymentMethod();

  const balance: number = getBalanceFromPaymentMethods(prepaidPaymentMethod!);

  const applePaySelected = isApplePay(reloadPaymentMethodId);
  const googlePaySelected = isGooglePay(reloadPaymentMethodId);

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      handleGiftCardReload(data);
      return;
    }
    if (error) {
      logger.warn(error);
    }
  };

  const { requestApplePayPayment } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestGooglePayPayment } = useGooglePay({
    onCompleted: (data: IGooglePayDetails) => {
      if (data) {
        handleGiftCardReload(data);
      }
    },
  });

  // reloadGiftCard reloads the Gift Card
  const handleGiftCardReload = useCallback(
    async (input?: IApplePayDetails | IGooglePayDetails) => {
      const reloadResult = await reloadGiftCard(reloadAmount, balance, input);

      if (typeof reloadResult === 'number') {
        setCurrentTotalTimBalance(reloadResult);
        setForceSuccessDialog(true);
        openLoadPrepaidCardDialog();
      }
    },
    [
      reloadGiftCard,
      setCurrentTotalTimBalance,
      openLoadPrepaidCardDialog,
      setForceSuccessDialog,
      balance,
      reloadAmount,
    ]
  );

  const handleReloadClick = useCallback(() => {
    if (applePaySelected) {
      return requestApplePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmount,
      });
    }
    if (googlePaySelected) {
      // Get the prepaid gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmount,
        gatewayMerchantId,
        gateway,
      });
    }
    return handleGiftCardReload();
  }, [
    applePaySelected,
    countryCode,
    currencyCode,
    googlePaySelected,
    reloadAmount,
    handleGiftCardReload,
    requestApplePayPayment,
    requestGooglePayPayment,
  ]);

  // Views
  // -----
  // Transform the data to fit our needs
  let cardNumber = getPrepaidCardNumber();

  const onPaymentMethodClose = () => {};
  const onPaymentMethodClick = () => {};

  return (
    <>
      <LoadPrepaidCardDialog
        forceSuccess={forceSuccessDialog}
        loadAmount={currentTotalTimBalance}
      />
      <Helmet title={formatMessage({ id: 'myTimsGiftCard' })} />
      <TimCardPage
        pageRoutes={routes}
        isLoadingGiftCard={paymentsLoading}
        giftCardNumberFormatted={cardNumber || ''}
        giftCardBallanceCents={balance}
        paymentMethods={{
          isLoading: paymentsLoading,
          selected: selectedReloadPaymentMethod,
          setPrepaidReloadPaymentMethodId: handleReloadPaymentMethodSelect,
          options: nonSelectedReloadPaymentMethods,
        }}
        giftCardReload={{
          isLoading: false,
          onSetActive: handleReloadClick,
          selected: reloadAmount,
          onSetSelected: (id: string | number) => setReloadAmount(Number(id)),
          options: RELOAD_AMOUNTS,
        }}
        onOpenTransferBallanceModal={openTransferBalanceDialog}
        onAddPaymentMethodClick={onPaymentMethodClick}
        onPaymentMethodClose={onPaymentMethodClose}
      />
    </>
  );
};

export default TimCard;
